<template>
  <div class="login-container">
    <div class="login-card">
      <h2 class="login-title">球杆售后系统</h2>
      <p class="login-subtitle">重置登录密码</p>

      <el-form
          ref="resetFormRef"
          :model="resetForm"
          :rules="resetRules"
          label-width="100%"
          label-position="top"
          class="login-form"
      >
        <!-- 手机号（只读展示） -->
        <el-form-item label="手机号">
          <el-input
              v-model="resetForm.phoneNumber"
              placeholder="手机号"
              prefix-icon="el-icon-phone"
              class="login-input"
              disabled
          ></el-input>
        </el-form-item>

        <!-- 新密码输入框 -->
        <el-form-item label="新密码" prop="newPassword">
          <el-input
              v-model="resetForm.newPassword"
              placeholder="请输入6-16位密码"
              prefix-icon="el-icon-lock"
              show-password
              class="login-input"
          ></el-input>
        </el-form-item>

        <!-- 确认新密码输入框 -->
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
              v-model="resetForm.confirmPassword"
              placeholder="请再次输入新密码"
              prefix-icon="el-icon-lock"
              show-password
              class="login-input"
          ></el-input>
        </el-form-item>

        <!-- 确认重置按钮 -->
        <el-form-item>
          <el-button
              type="primary"
              :loading="isLoading"
              @click="handleResetPassword"
              class="login-btn"
          >
            确认重置
          </el-button>
        </el-form-item>

        <!-- 返回登录入口 -->
        <div class="register-entry">
          <span class="register-link" @click="goBackLogin">返回登录</span>
        </div>
      </el-form>

      <!-- 提示弹框 -->
      <MessageDialog
          v-if="showDialog"
          title="提示"
          :content="messageContent"
          confirmText="我知道了"
          :showCancel="false"
          @confirm="handleDialogConfirm"
          @close="showDialog = false"
          closeOnMask
      />
    </div>
  </div>
</template>

<script>
import { updatePassword } from "@/api/sales";
import MessageDialog from "@/views/business/utils/messageDialog.vue";

export default {
  name: 'SalesResetPassword',
  components: { MessageDialog },
  data() {
    // 新密码校验规则
    const validateNewPassword = (rule, value, callback) => {
      if (!value) return callback(new Error('请输入新密码'))
      if (value.length < 6 || value.length > 16) {
        callback(new Error('密码长度需在6-16位之间'))
      } else {
        // 验证通过后，触发确认密码的校验
        if (this.resetForm.confirmPassword) {
          this.$refs.resetFormRef.validateField('confirmPassword')
        }
        callback()
      }
    }

    // 确认密码校验规则
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) return callback(new Error('请确认新密码'))
      if (value !== this.resetForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      // 表单数据
      resetForm: {
        phoneNumber: '', // 从路由参数获取
        newPassword: '',
        confirmPassword: ''
      },
      // 校验规则
      resetRules: {
        newPassword: [{ validator: validateNewPassword, trigger: 'blur' }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }]
      },
      isLoading: false,
      // 弹框相关
      showDialog: false,
      messageContent: ''
    }
  },
  mounted() {
    window.addEventListener('resize', this.adjustContainerHeight)
    this.adjustContainerHeight()
    // 获取路由中的手机号参数
    this.getPhoneNumberFromRoute()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustContainerHeight)
  },
  methods: {
    // 适配容器高度
    adjustContainerHeight() {
      const container = document.querySelector('.login-container')
      if (container) {
        container.style.minHeight = `${window.innerHeight}px`
      }
    },
    // 从路由参数获取手机号
    getPhoneNumberFromRoute() {
      const { phoneNumber } = this.$route.query
      if (!phoneNumber || !/^1[3-9]\d{9}$/.test(phoneNumber)) {
        // 无有效手机号，跳转回忘记密码页面
        this.showDialog = true
        this.messageContent = '验证信息失效，请重新验证'
        setTimeout(() => {
          this.$router.replace('/sales_forget_password')
        }, 2000)
        return
      }
      this.resetForm.phoneNumber = phoneNumber
    },
    // 处理重置密码
    async handleResetPassword() {
      try {
        // 表单校验
        await this.$refs.resetFormRef.validate()
      } catch (err) {
        this.showDialog = true
        this.messageContent = '请检查表单填写是否正确！'
        return
      }

      this.isLoading = true
      try {
        // 调用重置密码接口
        const res = await updatePassword({
          phoneNumber: this.resetForm.phoneNumber,
          password: this.resetForm.newPassword
        })
        if (res.code === 200) {
          this.showDialog = true
          this.messageContent = '密码重置成功，请返回登录'
        } else {
          this.showDialog = true
          this.messageContent = res.returnMsg || '密码重置失败，请重试'
        }
      } catch (error) {
        this.showDialog = true
        this.messageContent = error.message || '网络异常，请稍后再试'
      } finally {
        this.isLoading = false
      }
    },
    // 弹框确认按钮回调（重置成功后跳转登录）
    handleDialogConfirm() {
      this.showDialog = false
      if (this.messageContent.includes('成功')) {
        this.$router.replace('/sales_login')
      }
    },
    // 返回登录页
    goBackLogin() {
      this.$router.replace('/sales_login')
    }
  }
}
</script>

<style scoped lang="scss">
/* 复用统一的样式规范，仅调整少量适配 */
/* 全局基础 */
body {
  touch-action: manipulation;
  user-select: none;
  margin: 0;
  padding: 0;
}

/* 容器核心布局 */
.login-container {
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #409eff, #67c23a);
}

/* 登录卡片核心 */
.login-card {
  position: relative;
  z-index: 1;
  background: #fff;
  border-radius: 24px;
  box-sizing: border-box;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

/* 标题通用样式 */
.login-title {
  color: #333;
  text-align: center;
  margin: 0 0 10px 0;
  font-weight: 700;
  padding-left: 10px;
}

.login-subtitle {
  color: #666;
  text-align: center;
  margin-bottom: 20px;
  padding-left: 10px;
}

/* 输入框通用样式 */
.login-input {
  box-sizing: border-box;
  border: 1px solid #e5e5e5;
  border-radius: 18px;
  margin-bottom: 4px;
  padding: 0 56px 0 48px;
}

/* Element Input 通用深度样式 */
::v-deep .el-input__prefix {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 100%;
  transform: none;
}

::v-deep .el-input__prefix i {
  color: #409eff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

::v-deep .el-input__suffix {
  width: 40px;
  height: 40px;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
}

::v-deep .el-input__suffix i {
  color: #409eff;
  width: 100%;
  height: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
}

::v-deep .el-input__inner {
  height: 100%;
  line-height: 48px;
  font-size: 14px;
  border: none;
  padding: 0;
}

/* 禁用状态手机号输入框样式 */
::v-deep .el-input.is-disabled .el-input__inner {
  background-color: #f5f7fa;
  color: #666;
  cursor: not-allowed;
}

/* Element Form 通用深度样式 */
::v-deep .el-form {
  --el-form-border-color: transparent;
}

::v-deep .el-form-item__label {
  color: #555;
  margin-bottom: 10px;
  font-weight: 500;
}

::v-deep .el-form-item__error {
  font-size: 12px;
  padding-top: 2px;
  padding-left: 10px;
}

::v-deep .el-form-item {
  margin-bottom: 24px;
}

/* 确认重置按钮样式 */
.login-btn {
  border: none;
  border-radius: 10px;
  background: #409eff;
  color: #fff;
  width: 100%;
  height: 48px;
  font-size: 17px;
  transition: all 0.2s ease;
}

.login-btn:hover {
  background: #3390ff;
}

.login-btn:disabled {
  background: #b3d8ff;
}

/* 返回登录入口样式 */
.register-entry {
  display: flex;
  justify-content: center;
  cursor: pointer;
  padding: 8px 0;
}

.register-link {
  color: #409eff;
  position: relative;
  font-size: 14px;
}

.register-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background-color: #409eff;
  transition: width 0.3s ease;
}

.register-entry:hover .register-link::after {
  width: 100%;
}

/* ===================== 移动端适配（< 768px） ===================== */
@media screen and (max-width: 767px) {
  .login-container {
    width: 100vw;
    min-height: 100vh;
    padding: 0 20px;
  }

  .login-card {
    width: 90%;
    max-width: 400px;
    padding: 40px 25px;
  }

  .login-title {
    font-size: 26px;
    margin-bottom: 10px;
    padding-left: 10px;
  }

  .login-subtitle {
    font-size: 15px;
    margin-bottom: 20px;
    padding-left: 10px;
  }

  .login-input {
    height: 48px;
    font-size: 16px;
    border-radius: 18px;
    padding: 0 16px 0 48px;
    margin-bottom: 4px;
  }

  ::v-deep .el-form-item__label {
    font-size: 16px;
    margin-bottom: 10px;
  }

  .login-btn {
    height: 48px;
    font-size: 17px;
    border-radius: 10px;
  }
  /* 前缀图标居中 */
  ::v-deep .el-input__prefix {
    width: 48px;
    height: 100%;
  }

  ::v-deep .el-input__prefix i {
    font-size: 18px;
  }

  /* 输入框内容区 */
  ::v-deep .el-input__inner {
    height: 100%;
    line-height: 48px;
    font-size: 14px;
    padding: 0;
  }

  ::v-deep .el-form-item__error {
    font-size: 12px;
    padding-top: 2px;
    padding-left: 10px;
  }
  ::v-deep .el-form-item {
    margin-bottom: 24px;
  }
  /* 密码显隐按钮放大 */
  ::v-deep .el-input__suffix {
    width: 40px;
    height: 40px;
    right: 8px;
    top: 50%;
    padding: 0;
  }

  ::v-deep .el-input__suffix i {
    font-size: 24px;
    width: 100%;
    height: 100%;
  }
  .register-link {
    font-size: 14px;
  }
}

/* ===================== 平板适配（768px ≤ 宽度 < 1200px） ===================== */
@media screen and (min-width: 768px) and (max-width: 1199px) {
  .login-container {
    min-height: 100vh;
    padding: 0 40px;
  }

  .login-card {
    width: 70%;
    max-width: 500px;
    padding: 50px 40px;
    border-radius: 28px;
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.18);
  }

  .login-title {
    font-size: 30px;
    margin-bottom: 15px;
  }

  .login-subtitle {
    font-size: 16px;
    margin-bottom: 28px;
  }

  .login-input {
    height: 52px;
    font-size: 17px;
    border-radius: 20px;
  }

  ::v-deep .el-form-item__label {
    font-size: 17px;
    margin-bottom: 12px;
  }

  .login-btn {
    height: 52px;
    font-size: 18px;
    border-radius: 12px;
  }
}

/* ===================== PC 端适配（≥ 1200px） ===================== */
@media screen and (min-width: 1200px) {
  .login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #409eff 0%, #67c23a 100%);
    position: relative;
    padding: 0 50px;
  }

  .login-card {
    width: 100%;
    max-width: 520px;
    padding: 60px 50px;
    border-radius: 32px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .login-title {
    font-size: 32px;
    margin-bottom: 18px;
  }

  .login-subtitle {
    font-size: 17px;
    margin-bottom: 32px;
  }

  .login-input {
    height: 56px;
    font-size: 17px;
    border-radius: 22px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  ::v-deep .login-input:focus .el-input__inner {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
  }

  ::v-deep .el-form-item__label {
    font-size: 18px;
    margin-bottom: 14px;
  }

  .login-btn {
    height: 56px;
    font-size: 19px;
    border-radius: 14px;
  }

  .login-btn:hover {
    background: #3390ff;
    transform: scale(1.02);
  }

  .login-btn:active {
    transform: scale(0.98);
  }

  .register-link {
    font-size: 15px;
  }
}
</style>